/**
 * The due-date strip at the top of the Candidate Requests tab.
 *
 * Open reminders sorted by deadline (the store keeps them sorted), overdue in red — which also
 * puts overdue first, since ascending dueAt does that by itself. Each row: what to do, when it is
 * due, the thread's Gmail link when it came from a card, edit, done, delete.
 *
 * Always the SIGNED-IN user's own reminders, on EVERY board view — the header says so, so nobody
 * reads it as the board owner's task list. Not gated on isOwnBoard: a viewer who is not on the
 * recruiter roster never sees "their own" board at all, and their reminders must not vanish
 * with it.
 *
 * Renders nothing when there is nothing due — an empty box would be noise on every load. A failed
 * load also renders nothing: reminders are an aid, and the tab must not degrade over them.
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { COLORS } from './format';
import { ReminderModal } from './CreateReminderButton';
import { useReminders } from './useReminders';
import type { TaReminder } from '../../services/taRemindersApi';

const fmtDue = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const gmailUrl = (threadId: string) =>
  `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`;

function ReminderRow({
  reminder,
  now,
  onOpen,
  onEdit,
  onDone,
  onDelete,
}: {
  reminder: TaReminder;
  /** Sampled once per strip render — render functions must stay pure (react-compiler lint). */
  now: number;
  /** Focus the request IN THE TOOL (expand + scroll, or fall back to search). */
  onOpen?: () => void;
  onEdit: () => void;
  onDone: () => void;
  onDelete: () => void;
}) {
  const overdue = Date.parse(reminder.dueAt) < now;
  return (
    <li className="flex flex-wrap items-center gap-2 py-1.5">
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
        style={
          overdue
            ? { backgroundColor: COLORS.redBg, color: COLORS.redText }
            : { backgroundColor: '#EEF4FC', color: '#1D4ED8' }
        }
      >
        {overdue ? 'overdue · ' : ''}
        {fmtDue(reminder.dueAt)}
      </span>
      {/* Free text that names candidates — rendered as text, React escapes it. */}
      <span className="min-w-0 flex-1 truncate text-sm text-gray-800" title={reminder.title}>
        {reminder.title}
      </span>
      {reminder.threadId && onOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="shrink-0 text-xs font-semibold text-blue-700 hover:underline"
          title="Open the request this reminder came from, here in the tab"
        >
          ✉ Open request
        </button>
      )}
      {reminder.outreachId && (
        <a
          href={`/outreach?candidate=${encodeURIComponent(reminder.outreachId)}`}
          className="shrink-0 text-xs font-semibold text-blue-700 hover:underline"
          title="Open the outreach card this follow-up belongs to"
        >
          ↗ Open outreach
        </a>
      )}
      {reminder.threadId && (
        <a
          href={gmailUrl(reminder.threadId)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-xs text-gray-400 hover:underline"
          title="The same thread in Gmail"
        >
          Gmail ↗
        </a>
      )}
      <button
        type="button"
        onClick={onEdit}
        className="shrink-0 text-xs text-gray-500 hover:text-gray-800"
        title="Edit the reminder"
      >
        ✎ Edit
      </button>
      <button
        type="button"
        onClick={onDone}
        className="shrink-0 text-xs font-semibold text-green-700 hover:text-green-900"
        title="Done — remove it from the strip"
      >
        Done
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 text-gray-400 hover:text-red-700"
        title="Delete without completing"
        aria-label="Delete reminder"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

export function RemindersStrip({
  onOpenThread,
}: {
  /** Provided by RequestsTab: focus the request in the tool (expand + scroll, or search). */
  onOpenThread?: (threadId: string, candidateName: string | null) => void;
} = {}) {
  const { reminders, failed, reload, complete, remove } = useReminders();
  const [modal, setModal] = useState<'closed' | 'add' | TaReminder>('closed');
  // A ticking minute clock, in state (a bare Date.now() per row render is impure — react-compiler
  // lint) — without the tick, a 14:00 deadline never flipped to overdue while the tab stayed open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const open = reminders.filter((r) => r.status === 'open');

  // Say so when the load failed — the empty state would silently hide reminders that exist.
  if (failed) {
    return (
      <p className="text-right text-xs text-gray-500">
        Could not load your reminders just now.{' '}
        <button type="button" onClick={() => void reload()} className="underline hover:text-gray-800">
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-800">
          Your reminders{' '}
          <span className="font-normal text-gray-400">— only you see these</span>
        </p>
        <button
          type="button"
          onClick={() => setModal('add')}
          className="text-xs text-gray-500 underline hover:text-gray-800"
        >
          + Add
        </button>
      </div>
      {/* The empty state stays a VISIBLE box, not a collapsed link: this is where the feature
          lives, and a recruiter who has never used it has to be able to find it. One muted line
          that also says where the other entry point is. */}
      {open.length === 0 && (
        <p className="text-xs text-gray-400">
          Nothing due. Create one from any request card (Reminder) or with + Add.
        </p>
      )}
      <ul className="divide-y divide-gray-100">
        {open.map((r) => (
          <ReminderRow
            key={r.id}
            reminder={r}
            now={now}
            onOpen={
              onOpenThread && r.threadId
                ? () => onOpenThread(r.threadId as string, r.candidateName)
                : undefined
            }
            onEdit={() => setModal(r)}
            onDone={() => void complete(r)}
            onDelete={() => void remove(r)}
          />
        ))}
      </ul>
      {modal === 'add' && <ReminderModal open onClose={() => setModal('closed')} />}
      {modal !== 'closed' && modal !== 'add' && (
        <ReminderModal
          // Keyed by target: the modal seeds its form state on mount, so switching straight from
          // editing one reminder to another must remount it or the first one's text sticks.
          key={modal.id}
          open
          onClose={() => setModal('closed')}
          edit={{ id: modal.id, title: modal.title, dueAt: modal.dueAt }}
        />
      )}
    </div>
  );
}
